import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';

import {
  DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS,
  type BrandEmailTemplateListItem,
  type BrandSignatureFrameSettings,
  type BrandSummary,
  type UpdateBrandPayload,
} from '../../models/brand.models';
import { BrandApiService } from '../../services/brand-api.service';
import { UserDateTimeFormatService } from '../../services/user-date-time-format.service';
import { normalizeApiError } from '../../utils/error-normalizer';

type EditBrandTabId = 'general' | 'advanced' | 'email-content';

interface EditBrandDraft {
  name: string;
  isDefault: boolean;
  logoDataUrl?: string;
  logoFileName: string;
  emailDisplayName: string;
  emailBackgroundColor: string;
  primaryButtonColor: string;
  primaryButtonTextColor: string;
  showPlatformBranding: boolean;
  signatureFrame: BrandSignatureFrameSettings;
}

@Component({
  selector: 'app-edit-brand',
  imports: [CommonModule, FormsModule],
  templateUrl: './edit-brand.component.html',
  styleUrl: './edit-brand.component.scss',
})
export class EditBrandComponent implements OnInit {
  activeTab: EditBrandTabId = 'email-content';
  brand: BrandSummary | null = null;
  templates: BrandEmailTemplateListItem[] = [];
  loading = false;
  saving = false;
  dragActive = false;
  errorMessage = '';
  saveErrorMessage = '';
  nameErrorMessage = '';
  logoErrorMessage = '';

  draft: EditBrandDraft = this.createEmptyDraft();

  @ViewChild('logoInput') private logoInput?: ElementRef<HTMLInputElement>;

  private readonly brandsApi = inject(BrandApiService);
  private readonly dateTimeFormat = inject(UserDateTimeFormatService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly cdr = inject(ChangeDetectorRef);
  private brandId = '';

  ngOnInit(): void {
    const brandId = this.route.snapshot.paramMap.get('brandId')?.trim() ?? '';
    if (!brandId) {
      this.errorMessage = 'Brand not found.';
      return;
    }

    this.brandId = brandId;
    this.loading = true;
    this.errorMessage = '';

    forkJoin({
      brand: this.brandsApi.getBrand(brandId),
      templates: this.brandsApi.listBrandEmailTemplates(brandId),
    })
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: ({ brand, templates }) => {
          this.brand = brand;
          this.draft = this.createDraftFromBrand(brand);
          this.templates = templates.items;
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.errorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to load brand email templates. Please try again.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  goBack(): void {
    void this.router.navigate(['/settings/branding']);
  }

  selectTab(tab: EditBrandTabId): void {
    this.activeTab = tab;
    this.saveErrorMessage = '';
    this.nameErrorMessage = '';
  }

  get canSaveGeneral(): boolean {
    return (this.activeTab === 'general' || this.activeTab === 'advanced') && !this.saving && this.draft.name.trim().length > 0;
  }

  openTemplate(template: BrandEmailTemplateListItem): void {
    if (!this.brand) {
      return;
    }

    void this.router.navigate([
      '/settings/branding',
      this.brand.brandId,
      'edit',
      'email-content',
      template.emailTemplateType,
    ]);
  }

  formatLastModified(value: string | null): string {
    if (!value) {
      return '-';
    }

    return this.dateTimeFormat.formatDateTime(value);
  }

  triggerLogoBrowse(): void {
    this.logoInput?.nativeElement.click();
  }

  onLogoSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }

    void this.readLogoFile(file);
  }

  onLogoDragOver(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = true;
  }

  onLogoDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
  }

  onLogoDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragActive = false;
    const file = event.dataTransfer?.files?.[0];
    if (!file) {
      return;
    }

    void this.readLogoFile(file);
  }

  applyLogoDataUrl(dataUrl: string, fileName = 'brand-logo.png'): void {
    this.draft = {
      ...this.draft,
      logoDataUrl: dataUrl,
      logoFileName: fileName,
    };
    this.dragActive = false;
    this.logoErrorMessage = '';
    this.cdr.markForCheck();
  }

  clearLogo(): void {
    this.draft = {
      ...this.draft,
      logoDataUrl: undefined,
      logoFileName: '',
    };
    this.dragActive = false;
    this.logoErrorMessage = '';

    if (this.logoInput?.nativeElement) {
      this.logoInput.nativeElement.value = '';
    }
  }

  saveChanges(): void {
    this.nameErrorMessage = '';
    this.saveErrorMessage = '';

    const name = this.draft.name.trim();
    if (!name) {
      this.nameErrorMessage = 'Brand name is required.';
      return;
    }

    if (!this.brandId) {
      this.saveErrorMessage = 'Brand not found.';
      return;
    }

    const payload = this.buildUpdatePayload(name);

    this.saving = true;
    this.brandsApi
      .updateBrand(this.brandId, payload)
      .pipe(
        finalize(() => {
          this.saving = false;
          this.cdr.markForCheck();
        }),
      )
      .subscribe({
        next: (brand) => {
          this.brand = brand;
          this.draft = this.createDraftFromBrand(brand);
          this.cdr.markForCheck();
        },
        error: (error) => {
          this.saveErrorMessage = normalizeApiError(error, {
            fallbackMessage: 'Unable to save brand changes. Please try again.',
          }).message;
          this.cdr.markForCheck();
        },
      });
  }

  trackByTemplateType(_: number, item: BrandEmailTemplateListItem): string {
    return item.emailTemplateType;
  }

  private createDraftFromBrand(brand: BrandSummary): EditBrandDraft {
    return {
      name: brand.name,
      isDefault: brand.isDefault,
      logoDataUrl: brand.logoDataUrl,
      logoFileName: brand.logoDataUrl ? `${brand.name || 'brand'}-logo` : '',
      emailDisplayName: brand.emailDisplayName ?? '',
      emailBackgroundColor: brand.emailBackgroundColor,
      primaryButtonColor: brand.primaryButtonColor,
      primaryButtonTextColor: brand.primaryButtonTextColor,
      showPlatformBranding: brand.showPlatformBranding,
      signatureFrame: { ...(brand.signatureFrame ?? DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS) },
    };
  }

  private createEmptyDraft(): EditBrandDraft {
    return {
      name: '',
      isDefault: false,
      logoDataUrl: undefined,
      logoFileName: '',
      emailDisplayName: '',
      emailBackgroundColor: '#edf4ff',
      primaryButtonColor: '#0f62fe',
      primaryButtonTextColor: '#ffffff',
      showPlatformBranding: true,
      signatureFrame: { ...DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS },
    };
  }

  private async readLogoFile(file: File): Promise<void> {
    const acceptedTypes = new Set(['image/png', 'image/jpeg', 'image/jpg', 'image/svg+xml']);
    if (!acceptedTypes.has(file.type)) {
      this.logoErrorMessage = 'Only PNG, JPG, JPEG, and SVG files are supported.';
      return;
    }

    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
      reader.onerror = () => reject(new Error('Failed to read logo file'));
      reader.readAsDataURL(file);
    }).catch(() => '');

    if (!dataUrl) {
      this.logoErrorMessage = 'Unable to load this logo file. Please try another one.';
      return;
    }

    this.applyLogoDataUrl(dataUrl, file.name);
  }

  private buildUpdatePayload(name: string): UpdateBrandPayload {
    const payload: UpdateBrandPayload = {};
    const originalBrand = this.brand;
    const trimmedEmailDisplayName = this.draft.emailDisplayName.trim() || undefined;

    if (!originalBrand || name !== originalBrand.name) {
      payload.name = name;
    }

    if (!originalBrand || this.draft.isDefault !== originalBrand.isDefault) {
      payload.isDefault = this.draft.isDefault;
    }

    if (!originalBrand || trimmedEmailDisplayName !== originalBrand.emailDisplayName) {
      payload.emailDisplayName = trimmedEmailDisplayName;
    }

    if (!originalBrand || this.draft.emailBackgroundColor !== originalBrand.emailBackgroundColor) {
      payload.emailBackgroundColor = this.draft.emailBackgroundColor;
    }

    if (!originalBrand || this.draft.primaryButtonColor !== originalBrand.primaryButtonColor) {
      payload.primaryButtonColor = this.draft.primaryButtonColor;
    }

    if (!originalBrand || this.draft.primaryButtonTextColor !== originalBrand.primaryButtonTextColor) {
      payload.primaryButtonTextColor = this.draft.primaryButtonTextColor;
    }

    if (!originalBrand || this.draft.showPlatformBranding !== originalBrand.showPlatformBranding) {
      payload.showPlatformBranding = this.draft.showPlatformBranding;
    }

    const originalSignatureFrame = originalBrand?.signatureFrame ?? DEFAULT_BRAND_SIGNATURE_FRAME_SETTINGS;
    if (
      !originalBrand ||
      this.draft.signatureFrame.enabled !== originalSignatureFrame.enabled ||
      this.draft.signatureFrame.showRecipientName !== originalSignatureFrame.showRecipientName ||
      this.draft.signatureFrame.showRecipientEmail !== originalSignatureFrame.showRecipientEmail ||
      this.draft.signatureFrame.showTimestamp !== originalSignatureFrame.showTimestamp
    ) {
      payload.signatureFrame = { ...this.draft.signatureFrame };
    }

    if (!originalBrand) {
      payload.logoDataUrl = this.draft.logoDataUrl;
      return payload;
    }

    if (this.draft.logoDataUrl !== originalBrand.logoDataUrl) {
      payload.logoDataUrl = this.draft.logoDataUrl ?? null;
    }

    return payload;
  }
}
